import React, { useEffect, useState, useMemo } from "react";
import "./DealCalculator.css";
import { getAllBuyers, getAllSellers } from "../../services/Users/User.service";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const AdminDealCalculator: React.FC = () => {
    const navigate = useNavigate();

    const [buyers, setBuyers] = useState<any[]>([]);
    const [sellers, setSellers] = useState<any[]>([]);
    const [selectedBuyer, setSelectedBuyer] = useState("");
    const [selectedSeller, setSelectedSeller] = useState("");

    // Search states
    const [buyerSearchTerm, setBuyerSearchTerm] = useState("");
    const [sellerSearchTerm, setSellerSearchTerm] = useState("");
    const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);
    const [showSellerDropdown, setShowSellerDropdown] = useState(false);

    const [metalTypes, setMetalTypes] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [articles, setArticles] = useState<any[]>([]);
    const [uoms, setUoms] = useState<any[]>([]); // ✅ New state for UOMs

    // Seller-specific items (products)
    const [sellerItems, setSellerItems] = useState<any[]>([]);
    const [loadingSellerItems, setLoadingSellerItems] = useState(false);

    const [selectedMetalId, setSelectedMetalId] = useState<number | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
    const [selectedUomId, setSelectedUomId] = useState<number | null>(null); // ✅ New state for selected UOM

    const [selectedMetalData, setSelectedMetalData] = useState<any>(null);
    const [availableKaratOptions, setAvailableKaratOptions] = useState<any[]>([]);
    const [availableSilverPurities, setAvailableSilverPurities] = useState<string[]>([]);

    const [selectedKarat, setSelectedKarat] = useState("");
    const [purity, setPurity] = useState("");

    // Input fields for calculation
    const [priceInput, setPriceInput] = useState<string>("0");
    const [weightInput, setWeightInput] = useState<string>("0");
    const [quantityInput, setQuantityInput] = useState<string>("1");
    const [wastageInput, setWastageInput] = useState<string>("0");
    const [commissionInput, setCommissionInput] = useState<string>("0");
    const [makingChargesInput, setMakingChargesInput] = useState<string>("0");
    const [wastageMakingChargesInput, setWastageMakingChargesInput] = useState<string>("0");
    const [gstInput, setGstInput] = useState<string>("0");

    // Calculation states - Seller Invoice
    const [originalTunchSeller, setOriginalTunchSeller] = useState<number>(0);
    const [fineValue, setFineValue] = useState<number>(0);
    const [calculatedPriceValue, setCalculatedPriceValue] = useState<number>(0);
    const [sellerSubtotal, setSellerSubtotal] = useState<number>(0);
    const [sellerGstAmount, setSellerGstAmount] = useState<number>(0);
    const [sellerGrandTotal, setSellerGrandTotal] = useState<number>(0);

    // Calculation states - Buyer Invoice
    const [originalTunchBuyer, setOriginalTunchBuyer] = useState<number>(0);
    const [fineValueBuyer, setFineValueBuyer] = useState<number>(0);
    const [calculatedPriceValueBuyer, setCalculatedPriceValueBuyer] = useState<number>(0);
    const [subtotal, setSubtotal] = useState<number>(0);
    const [gstAmount, setGstAmount] = useState<number>(0);
    const [grandTotal, setGrandTotal] = useState<number>(0);

    // perGramPrice state for fine calculation
    const [perGramPrice, setPerGramPrice] = useState<number>(0);

    // Payment method state
    const [selectedPayment, setSelectedPayment] = useState("COD");

    const [categorySearchTerm, setCategorySearchTerm] = useState("");
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

    const [articleSearchTerm, setArticleSearchTerm] = useState("");
    const [showArticleDropdown, setShowArticleDropdown] = useState(false);

    // Admin profit states
    const [marginWastageProfit, setMarginWastageProfit] = useState<number>(0);
    const [marginMakingChargesProfit, setMarginMakingChargesProfit] = useState<number>(0);
    const [totalAdminProfit, setTotalAdminProfit] = useState<number>(0);

    // Buyer and seller details
    const [selectedBuyerData, setSelectedBuyerData] = useState<any>(null);
    const [selectedSellerData, setSelectedSellerData] = useState<any>(null);

    const userData = localStorage.getItem("user");
    const currentUser = userData ? JSON.parse(userData) : null;
    const currentUserRoleId = currentUser?.role_id || null;
    const isSuperAdmin = currentUserRoleId === "1";

    // Combine buyers and sellers for buyer dropdown
    const allBuyerCandidates = useMemo(() => {
        const map = new Map();
        buyers.forEach(b => map.set(b.id, { ...b, source: 'buyer' }));
        sellers.forEach(s => {
            if (!map.has(s.id)) {
                map.set(s.id, { ...s, source: 'seller' });
            }
        });
        return Array.from(map.values());
    }, [buyers, sellers]);

    const filteredBuyerCandidates = allBuyerCandidates.filter(user => {
        const searchLower = buyerSearchTerm.toLowerCase();
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const mobileNumber = (user.mobileNumber || '').toLowerCase();
        const businessName = (user.businessName || '').toLowerCase();
        return fullName.includes(searchLower) ||
            mobileNumber.includes(searchLower) ||
            businessName.includes(searchLower);
    });

    const filteredSellers = sellers.filter(seller => {
        const searchLower = sellerSearchTerm.toLowerCase();
        const fullName = `${seller.firstName || ''} ${seller.lastName || ''}`.toLowerCase();
        const mobileNumber = (seller.mobileNumber || '').toLowerCase();
        const businessName = (seller.businessName || '').toLowerCase();
        return fullName.includes(searchLower) ||
            mobileNumber.includes(searchLower) ||
            businessName.includes(searchLower);
    });

    // ✅ Filter categories by selected metal type
    const filteredCategories = categories.filter((cat: any) => {
        const categoryMetalId = cat.metalTypeId || cat.MetalTypeId || cat.metal_type_id;
        if (selectedMetalId) {
            return String(categoryMetalId) === String(selectedMetalId);
        }
        return true;
    }).filter((cat: any) =>
        cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    // ✅ Extract unique articles from sellerItems
    const sellerArticles = useMemo(() => {
        if (sellerItems.length > 0) {
            const articleMap = new Map();
            sellerItems.forEach((item: any) => {
                let articleId = item.ArticleId;
                let articleName = item.articleName;
                if (item.article_master?.articleName) {
                    articleName = item.article_master.articleName;
                    articleId = item.ArticleId;
                }
                if (articleName && articleId) {
                    articleMap.set(articleId, {
                        id: articleId,
                        articleName: articleName,
                        categoryId: item.categoryId,
                        metalTypeId: item.MetalTypeId,
                    });
                }
            });
            return Array.from(articleMap.values());
        }
        return [];
    }, [sellerItems]);

    // ✅ Filter seller articles by selected category + search term
    const filteredSellerArticles = sellerArticles.filter((art: any) => {
        if (selectedCategoryId && art.categoryId !== selectedCategoryId) {
            return false;
        }
        return art.articleName?.toLowerCase().includes(articleSearchTerm.toLowerCase());
    });

    // Formatting functions
    const formatNumberIN = (value: number): string => {
        const safeValue = Number.isFinite(value) ? value : 0;
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(safeValue);
    };

    const formatINR = (value: number): string => `₹${formatNumberIN(value)}`;

    const loadUsers = async () => {
        try {
            const buyerList = await getAllBuyers();
            const sellerList = await getAllSellers();
            setBuyers(buyerList || []);
            const approvedSellers = (sellerList || []).filter(
                (s: any) => s.approvalStatus === "approved"
            );
            setSellers(approvedSellers);
        } catch (error) {
            console.error("Failed to load users", error);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const metals = await axios.get("https://api.sonachandiwale.com/api/v1/metal/Activelist");
            const categoriesRes = await axios.get("https://api.sonachandiwale.com/api/v1/activeCategories");
            const articlesRes = await axios.get("https://api.sonachandiwale.com/api/v1/admin/Activearticles");
            // ✅ Fetch UOMs
            const uomsRes = await axios.get("https://api.sonachandiwale.com/api/v1/admin/get_All_UOM");
            
            setMetalTypes(metals.data.data || []);
            setCategories(categoriesRes.data.data || []);
            setArticles(articlesRes.data.data || []);
            setUoms(uomsRes.data.data || []); // ✅ Set UOMs
            
            // ✅ Set default UOM if available
            if (uomsRes.data.data && uomsRes.data.data.length > 0) {
                setSelectedUomId(uomsRes.data.data[0].id);
            }
        } catch (error) {
            console.error("Dropdown API error", error);
        }
    };

    // Fetch seller's items when seller is selected
    const fetchSellerItems = async (sellerId: number) => {
        try {
            setLoadingSellerItems(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`https://api.sonachandiwale.com/api/v1/items/${sellerId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setSellerItems(response.data.data || []);
            } else {
                setSellerItems([]);
            }
        } catch (error) {
            console.error("Error fetching seller items:", error);
            setSellerItems([]);
            toast.error("Failed to load seller's products");
        } finally {
            setLoadingSellerItems(false);
        }
    };

    const location = useLocation();

    useEffect(() => {
        try {
            let dealData: any = null;
            const params = new URLSearchParams(location.search);
            const dataParam = params.get("data");
            if (dataParam) {
                dealData = JSON.parse(decodeURIComponent(dataParam));
            }
            if (!dealData) return;

            if (dealData.sellerId) {
                setSelectedSeller(dealData.sellerId.toString());
                setSellerSearchTerm(dealData.sellerName || "");
                fetchSellerItems(dealData.sellerId);
            }
            if (dealData.category) {
                setCategorySearchTerm(dealData.category);
                const foundCategory = categories.find(
                    (c: any) => c.name?.toLowerCase() === dealData.category?.toLowerCase()
                );
                if (foundCategory) setSelectedCategoryId(foundCategory.id);
            }
            if (dealData.articleName) {
                setArticleSearchTerm(dealData.articleName);
            }
            if (dealData.metalType) {
                const foundMetal = metalTypes.find(
                    (m: any) => m.name?.toLowerCase() === dealData.metalType?.toLowerCase()
                );
                if (foundMetal) {
                    setSelectedMetalId(foundMetal.id);
                    setSelectedMetalData(foundMetal);
                }
            }
            if (dealData.purity) setPurity(dealData.purity.toString());
            if (dealData.wastage) setWastageInput(dealData.wastage.toString());
            if (dealData.makingCharges) setMakingChargesInput(dealData.makingCharges.toString());
        } catch (error) {
            console.error("Error parsing deal data", error);
        }
    }, [location.search, categories, articles, metalTypes]);

    // When seller changes, fetch items
    useEffect(() => {
        if (selectedSeller) {
            const sellerId = parseInt(selectedSeller);
            if (!isNaN(sellerId)) {
                fetchSellerItems(sellerId);
                const seller = sellers.find(s => s.id === sellerId);
                if (seller) {
                    setSelectedSellerData(seller);
                    setSellerSearchTerm(`${seller.firstName || ''} ${seller.lastName || ''} - ${seller.mobileNumber || ''}`);
                }
            }
        } else {
            setSellerItems([]);
            setSelectedArticleId(null);
            setSelectedCategoryId(null);
        }
    }, [selectedSeller, sellers]);

    // When seller items load and there is a pre-filled article name, select it
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const dataParam = params.get("data");
        if (dataParam && sellerItems.length > 0) {
            const dealData = JSON.parse(decodeURIComponent(dataParam));
            if (dealData.articleName) {
                const foundArticle = sellerItems.find((item: any) => {
                    const artName = item.article_master?.articleName || item.articleName;
                    return artName?.toLowerCase() === dealData.articleName?.toLowerCase();
                });
                if (foundArticle) {
                    setSelectedArticleId(foundArticle.ArticleId);
                    setArticleSearchTerm(dealData.articleName);
                }
            }
        }
    }, [sellerItems, location.search]);

    useEffect(() => {
        if (!sellers.length) return;
        const params = new URLSearchParams(location.search);
        const dataParam = params.get("data");
        if (!dataParam) return;
        const dealData = JSON.parse(decodeURIComponent(dataParam));
        if (dealData.sellerId) {
            const foundSeller = sellers.find((s: any) => s.id === dealData.sellerId);
            if (foundSeller) {
                setSelectedSeller(foundSeller.id.toString());
                setSelectedSellerData(foundSeller);
                setSellerSearchTerm(
                    `${foundSeller.firstName || ''} ${foundSeller.lastName || ''} - ${foundSeller.mobileNumber || ''}`
                );
            }
        }
    }, [sellers, location.search]);

    useEffect(() => {
        if (!allBuyerCandidates.length) return;
        const params = new URLSearchParams(location.search);
        const dataParam = params.get("data");
        if (!dataParam) return;
        const dealData = JSON.parse(decodeURIComponent(dataParam));
        if (dealData.buyerId) {
            const foundBuyer = allBuyerCandidates.find((b: any) => b.id === dealData.buyerId);
            if (foundBuyer) {
                setSelectedBuyer(foundBuyer.id.toString());
                setSelectedBuyerData(foundBuyer);
                setBuyerSearchTerm(
                    `${foundBuyer.firstName || ''} ${foundBuyer.lastName || ''} - ${foundBuyer.mobileNumber || ''}`
                );
            }
        }
    }, [allBuyerCandidates, location.search]);

    useEffect(() => {
        loadUsers();
        fetchDropdownData();
    }, []);

    useEffect(() => {
        setPerGramPrice(parseFloat(priceInput) || 0);
    }, [priceInput]);

    const handleMetalChange = (e: any) => {
        const id = Number(e.target.value);
        setSelectedMetalId(id);
        const metal = metalTypes.find((m: any) => m.id === id);
        setSelectedMetalData(metal);
        setSelectedCategoryId(null);
        setCategorySearchTerm("");
        setSelectedArticleId(null);
        setArticleSearchTerm("");
    };

    useEffect(() => {
        if (selectedMetalData && selectedMetalData.metal_purity_carat_masters) {
            const masters = selectedMetalData.metal_purity_carat_masters;
            const metalName = selectedMetalData.name?.toLowerCase();
            if (metalName === "gold") {
                const karats = masters
                    .filter((m: any) => m.karat)
                    .map((m: any) => ({ karat: m.karat, purity: m.purity }));
                setAvailableKaratOptions(karats);
            } else if (metalName === "silver") {
                const purities = masters
                    .filter((m: any) => m.purity)
                    .map((m: any) => m.purity);
                setAvailableSilverPurities(purities);
                if (purities.length > 0) setPurity(purities[0]);
            }
        }
    }, [selectedMetalData]);

    const handleKaratChange = (e: any) => {
        const karat = e.target.value;
        setSelectedKarat(karat);
        const selected = availableKaratOptions.find((k: any) => k.karat === karat);
        if (selected) setPurity(selected.purity);
    };

    // Calculations (same as before)
    useEffect(() => {
        const purityNum = parseFloat(purity) || 0;
        const weightNum = parseFloat(weightInput) || 0;
        const priceNum = parseFloat(priceInput) || 0;
        const wastageNum = parseFloat(wastageInput) || 0;
        const makingChargesAmount = (parseFloat(makingChargesInput) || 0) * weightNum;
        const gstPercent = parseFloat(gstInput) || 0;

        const tunchSeller = purityNum + wastageNum;
        const fine = (tunchSeller * weightNum) / 100;
        const priceVal = priceNum * fine;
        const sellerSub = priceVal + makingChargesAmount;
        const sellerGst = sellerSub * (gstPercent / 100);
        const sellerTotal = sellerSub + sellerGst;

        setOriginalTunchSeller(tunchSeller);
        setFineValue(fine);
        setCalculatedPriceValue(sellerSub);
        setSellerSubtotal(sellerSub);
        setSellerGstAmount(sellerGst);
        setSellerGrandTotal(sellerTotal);
    }, [purity, weightInput, priceInput, wastageInput, makingChargesInput, gstInput]);

    useEffect(() => {
        const purityNum = parseFloat(purity) || 0;
        const wastageNum = parseFloat(wastageInput) || 0;
        const marginNum = parseFloat(commissionInput) || 0;
        const weightNum = parseFloat(weightInput) || 0;
        const priceNum = parseFloat(priceInput) || 0;
        const gstPercent = parseFloat(gstInput) || 0;
        const makingChargesAmount = (parseFloat(makingChargesInput) || 0) * weightNum;
        const wastageMakingChargesAmount = (parseFloat(wastageMakingChargesInput) || 0) * weightNum;

        const tunchSeller = purityNum + wastageNum;
        const tunchBuyer = tunchSeller + marginNum;
        const fineBuyer = (tunchBuyer * weightNum) / 100;
        const fineSeller = (tunchSeller * weightNum) / 100;
        const buyerSubtotal = (priceNum * fineBuyer) + makingChargesAmount + wastageMakingChargesAmount;
        const gstBuyer = buyerSubtotal * (gstPercent / 100);
        const buyerGrandTotal = buyerSubtotal + gstBuyer;
        const marginWastageProfitCalc = (fineBuyer - fineSeller) * priceNum;
        const marginMakingChargesProfitCalc = wastageMakingChargesAmount;

        setOriginalTunchBuyer(tunchBuyer);
        setFineValueBuyer(fineBuyer);
        setCalculatedPriceValueBuyer(buyerSubtotal - makingChargesAmount - wastageMakingChargesAmount);
        setSubtotal(buyerSubtotal);
        setGstAmount(gstBuyer);
        setGrandTotal(buyerGrandTotal);
        setMarginWastageProfit(marginWastageProfitCalc);
        setMarginMakingChargesProfit(marginMakingChargesProfitCalc);
        setTotalAdminProfit(marginWastageProfitCalc + marginMakingChargesProfitCalc);
    }, [purity, wastageInput, commissionInput, weightInput, priceInput, gstInput, makingChargesInput, wastageMakingChargesInput]);

    const sellerFineFromTotal = perGramPrice > 0 ? sellerSubtotal / perGramPrice : 0;
    const buyerFineFromTotal = perGramPrice > 0 ? subtotal / perGramPrice : 0;

    const handleBuyerSelect = (user: any) => {
        const userId = user.id.toString();
        if (selectedSeller && selectedSeller === userId) {
            toast.error("Buyer and seller cannot be the same person. Please select a different seller.");
            return;
        }
        setSelectedBuyer(userId);
        setSelectedBuyerData(user);
        setBuyerSearchTerm(`${user.firstName || ''} ${user.lastName || ''} - ${user.mobileNumber || ''}`.trim());
        setShowBuyerDropdown(false);
    };

    const handleSellerSelect = (seller: any) => {
        const sellerId = seller.id.toString();
        if (selectedBuyer && selectedBuyer === sellerId) {
            toast.error("Buyer and seller cannot be the same person. Please select a different buyer.");
            return;
        }
        setSelectedSeller(sellerId);
        setSelectedSellerData(seller);
        setSellerSearchTerm(`${seller.firstName || ''} ${seller.lastName || ''} - ${seller.mobileNumber || ''}`.trim());
        setShowSellerDropdown(false);
        setSelectedArticleId(null);
        setArticleSearchTerm("");
    };

    const handleGenerateInvoice = async () => {
        try {
            if (!selectedBuyer) { toast.error("Please select a buyer"); return; }
            if (!selectedSeller) { toast.error("Please select a seller"); return; }
            if (selectedBuyer === selectedSeller) {
                toast.error("Buyer and seller cannot be the same person. Please select different users.");
                return;
            }
            if (!selectedMetalId) { toast.error("Please select a metal type"); return; }
            if (!selectedCategoryId) { toast.error("Please select a category"); return; }
            if (!selectedArticleId) { toast.error("Please select an article"); return; }
            if (!selectedUomId) { toast.error("Please select a UOM"); return; } // ✅ UOM validation
            if (!purity) { toast.error("Please enter purity"); return; }
            if (!priceInput || parseFloat(priceInput) <= 0) { toast.error("Please enter a valid price"); return; }
            if (!weightInput || parseFloat(weightInput) <= 0) { toast.error("Please enter a valid weight"); return; }
            if (!quantityInput || parseInt(quantityInput) <= 0) { toast.error("Please enter a valid quantity"); return; }
            if (!selectedPayment) { toast.error("Please select a payment method"); return; }

            const weight = parseFloat(weightInput) || 0;
            const quantity = parseInt(quantityInput) || 1;
            const wastagePercent = parseFloat(wastageInput) || 0;
            const makingCharges = parseFloat(makingChargesInput) || 0;
            const wastageMakingCharges = parseFloat(wastageMakingChargesInput) || 0;
            const commissionPercent = parseFloat(commissionInput) || 0;
            const gstPercent = parseFloat(gstInput) || 0;
            const MetalSilverPay = subtotal / (parseFloat(priceInput) || 0);

            let karatValue = 0;
            if (selectedMetalData?.name === "Gold" && selectedKarat) {
                karatValue = parseFloat(selectedKarat.replace('K', ''));
            }

            const buyerInfo = {
                id: selectedBuyerData?.id || 0,
                name: selectedBuyerData?.firstName + ' ' + (selectedBuyerData?.lastName || '') || "Unknown Buyer",
                businessName: selectedBuyerData?.businessName || null,
                phone: selectedBuyerData?.mobileNumber || "-"
            };

            const sellerInfo = {
                id: selectedSellerData?.id || 0,
                name: selectedSellerData?.firstName + ' ' + (selectedSellerData?.lastName || '') || "Unknown Seller",
                businessName: selectedSellerData?.businessName || null,
                phone: selectedSellerData?.mobileNumber || "-"
            };

            const requestBody = {
                matchSellerId: sellerInfo.id,
                sellerBusinessName: sellerInfo.businessName,
                buyerId: buyerInfo.id,
                buyerName: buyerInfo.name,
                categoryId: selectedCategoryId,
                articleId: selectedArticleId,
                uomId: selectedUomId, // ✅ Dynamic UOM
                metalTypeId: selectedMetalId,
                purity: purity,
                karat: karatValue,
                carat: 0,
                centValue: 0,
                quantity: weight,
                weight: quantity,
                wastagePercent: wastagePercent,
                makingChargesPerGram: makingCharges,
                WastageMakingChargesPerGram: wastageMakingCharges,
                commissionPercent: commissionPercent,
                gstPercent: gstPercent,
                gstAmount: gstAmount,
                PaymentMethod: selectedPayment,
                buyerFine: buyerFineFromTotal,
                sellerfine: sellerFineFromTotal,
                buyerPurity: originalTunchBuyer,
                sellerPurity: originalTunchSeller,
                buyertotalAmount: subtotal,
                buyertotalAmmoumtGst: grandTotal,
                sellertotalAmount: sellerSubtotal,
                priceVisibility: true,
                totalAmount: grandTotal,
                sellerPrice: sellerSubtotal,
                buyerPrice: calculatedPriceValueBuyer,
                perGmPrice: parseFloat(priceInput) || 0,
                MetalSilverPay: MetalSilverPay,
                marginWastageProfit: marginWastageProfit,
                marginMakingChargesProfit: marginMakingChargesProfit,
                totalAdminProfit: totalAdminProfit,
            };

            const token = localStorage.getItem('token');
            if (!token) { toast.error("Authentication token not found. Please login again."); return; }

            const response = await axios.post(
                'https://api.sonachandiwale.com/api/v1/admin/create-invoice',
                requestBody,
                { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
            );

            const data = response.data;
            if (data.success) {
                toast.success("Order created successfully!");
                navigate("/dashboard");
            } else {
                toast.error(data.message || "Failed to create Order");
            }
        } catch (error) {
            console.error("Error creating invoice:", error);
            if (axios.isAxiosError(error) && error.response) {
                toast.error(error.response.data.message || "An error occurred while creating Order");
            } else {
                toast.error("An error occurred while creating Order");
            }
        }
    };

    return (
        <div className="deal-calculator">
            <h1 className="main-title">Deal Calculator</h1>
            <p className="main-subtitle">Calculate deal values and generate Order</p>

            <div className="top-sections">
                {/* LEFT SECTION - Deal Parameters */}
                <div className="section">
                    <h2 className="section-title">Deal Parameters</h2>
                    <p className="section-subtitle">Enter the deal details to calculate</p>

                    <div className="form-grid">
                        {/* BUYER */}
                        <div className="form-group">
                            <label>Buyer Name</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={buyerSearchTerm}
                                    onChange={(e) => {
                                        setBuyerSearchTerm(e.target.value);
                                        setShowBuyerDropdown(true);
                                        if (e.target.value === "") { setSelectedBuyer(""); setSelectedBuyerData(null); }
                                    }}
                                    onFocus={() => setShowBuyerDropdown(true)}
                                    placeholder="Search buyer by name, phone, or business..."
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                                />
                                {showBuyerDropdown && filteredBuyerCandidates.length > 0 && (
                                    <>
                                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowBuyerDropdown(false)} />
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 999, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                            {filteredBuyerCandidates.map((user: any) => (
                                                <div key={user.id} onClick={() => handleBuyerSelect(user)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                    <div style={{ fontWeight: '500', color: "#000" }}>{user.firstName} {user.lastName}{user.businessName && ` (${user.businessName})`}</div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>📞 {user.mobileNumber}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* SELLER */}
                        <div className="form-group">
                            <label>Seller Name</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={sellerSearchTerm}
                                    onChange={(e) => {
                                        setSellerSearchTerm(e.target.value);
                                        setShowSellerDropdown(true);
                                        if (e.target.value === "") { setSelectedSeller(""); setSelectedSellerData(null); setSellerItems([]); }
                                    }}
                                    onFocus={() => setShowSellerDropdown(true)}
                                    placeholder="Search seller by name, phone, or business..."
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                                />
                                {showSellerDropdown && filteredSellers.length > 0 && (
                                    <>
                                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowSellerDropdown(false)} />
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 999, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                            {filteredSellers.map((seller: any) => (
                                                <div key={seller.id} onClick={() => handleSellerSelect(seller)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                    <div style={{ fontWeight: '500', color: "#000" }}>{seller.firstName} {seller.lastName}{seller.businessName && ` (${seller.businessName})`}</div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>📞 {seller.mobileNumber}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* METAL TYPE */}
                        <div className="form-group">
                            <label>Metal Type</label>
                            <select value={selectedMetalId || ""} onChange={handleMetalChange}>
                                <option value="">Select Metal</option>
                                {metalTypes.map((metal: any) => (
                                    <option key={metal.id} value={metal.id}>{metal.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* ✅ UOM DROPDOWN - Added after Metal Type */}
                        <div className="form-group">
                            <label>Unit of Measurement (UOM)</label>
                            <select 
                                value={selectedUomId || ""} 
                                onChange={(e) => setSelectedUomId(Number(e.target.value))}
                            >
                                <option value="">Select UOM</option>
                                {uoms.map((uom: any) => (
                                    <option key={uom.id} value={uom.id}>
                                        {uom.name || uom.uomName || uom.unit || `UOM ${uom.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* GOLD KARAT */}
                        {selectedMetalData?.name === "Gold" && (
                            <div className="form-group">
                                <label>Karat</label>
                                <select value={selectedKarat} onChange={handleKaratChange}>
                                    <option value="">Select Karat</option>
                                    {availableKaratOptions.map((k: any, i: number) => (
                                        <option key={i} value={k.karat}>{k.karat} ({k.purity})</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* CATEGORY - filtered by metal type */}
                        <div className="form-group">
                            <label>Category</label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="text"
                                    value={categorySearchTerm}
                                    onChange={(e) => {
                                        setCategorySearchTerm(e.target.value);
                                        setShowCategoryDropdown(true);
                                        if (e.target.value === "") setSelectedCategoryId(null);
                                    }}
                                    onFocus={() => setShowCategoryDropdown(true)}
                                    placeholder="Search category..."
                                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px" }}
                                    disabled={!selectedMetalId}
                                />
                                {showCategoryDropdown && filteredCategories.length > 0 && selectedMetalId && (
                                    <>
                                        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowCategoryDropdown(false)} />
                                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#ffffff", color: "#000000", border: "1px solid #0e0909ff", borderRadius: "4px", marginTop: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 999, boxShadow: '0 2px 8px rgba(22, 17, 17, 0.15)' }}>
                                            {filteredCategories.map((cat: any) => (
                                                <div
                                                    key={cat.id}
                                                    onClick={() => {
                                                        setSelectedCategoryId(cat.id);
                                                        setCategorySearchTerm(cat.name);
                                                        setShowCategoryDropdown(false);
                                                        setSelectedArticleId(null);
                                                        setArticleSearchTerm("");
                                                    }}
                                                    style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                                                >
                                                    {cat.name}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ARTICLE - filtered by seller and category */}
                        <div className="form-group">
                            <label>Article</label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type="text"
                                    value={articleSearchTerm}
                                    onChange={(e) => {
                                        setArticleSearchTerm(e.target.value);
                                        setShowArticleDropdown(true);
                                        if (e.target.value === "") setSelectedArticleId(null);
                                    }}
                                    onFocus={() => setShowArticleDropdown(true)}
                                    placeholder={selectedSeller ? "Search seller's articles..." : "Select a seller first"}
                                    disabled={!selectedSeller || loadingSellerItems || filteredSellerArticles.length === 0}
                                    style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px" }}
                                />
                                {showArticleDropdown && filteredSellerArticles.length > 0 && selectedSeller && (
                                    <>
                                        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowArticleDropdown(false)} />
                                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, color: "#000000", background: "#fff", border: "1px solid #ddd", borderRadius: "4px", marginTop: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 999 }}>
                                            {filteredSellerArticles.map((art: any) => (
                                                <div
                                                    key={art.id}
                                                    onClick={() => {
                                                        setSelectedArticleId(art.id);
                                                        setArticleSearchTerm(art.articleName);
                                                        setShowArticleDropdown(false);
                                                    }}
                                                    style={{ padding: "10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                                                >
                                                    {art.articleName}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {loadingSellerItems && <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Loading seller's products...</div>}
                                {!loadingSellerItems && selectedSeller && filteredSellerArticles.length === 0 && (
                                    <div style={{ fontSize: '12px', color: '#F59E0B', marginTop: '4px' }}>No articles found for this seller and category.</div>
                                )}
                            </div>
                        </div>

                        {/* PURITY */}
                        <div className="form-group">
                            <label>Purity (%)</label>
                            <input type="number" value={purity} onChange={(e) => setPurity(e.target.value)} placeholder="Enter purity" />
                        </div>

                        {/* PRICE */}
                        <div className="form-group">
                            <label>Current Metal Price (per/gm)</label>
                            <input type="number" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} />
                        </div>

                        {/* WEIGHT */}
                        <div className="form-group">
                            <label>Purchase Quantity (per/gm)</label>
                            <input type="number" value={weightInput} onChange={(e) => setWeightInput(e.target.value)} />
                        </div>

                        {/* WASTAGE */}
                        <div className="form-group">
                            <label>Wastage (%)</label>
                            <input type="number" value={wastageInput} onChange={(e) => setWastageInput(e.target.value)} />
                        </div>

                        {/* COMMISSION */}
                        <div className="form-group">
                            <label>UP Wastage Margin (%)</label>
                            <input type="number" value={commissionInput} onChange={(e) => setCommissionInput(e.target.value)} />
                        </div>

                        {/* MAKING CHARGES */}
                        <div className="form-group">
                            <label>Making Charges (Per Gm) <span style={{ color: '#10B981', fontSize: '12px' }}>(Adds to Seller)</span></label>
                            <input type="number" value={makingChargesInput} onChange={(e) => setMakingChargesInput(e.target.value)} placeholder="Enter making charges" />
                        </div>

                        {/* MARGIN MAKING CHARGES */}
                        <div className="form-group">
                            <label>Margin Making Charges (Per Gm) <span style={{ color: '#FBBF24', fontSize: '12px' }}>(Adds to Buyer & Admin)</span></label>
                            <input type="number" value={wastageMakingChargesInput} onChange={(e) => setWastageMakingChargesInput(e.target.value)} placeholder="Enter margin making charges" />
                        </div>

                        {/* GST */}
                        <div className="form-group">
                            <label>GST (%)</label>
                            <input type="number" value={gstInput} onChange={(e) => setGstInput(e.target.value)} />
                        </div>
                    </div>
                </div>

                {/* RIGHT SECTION - Deal Summary (unchanged) */}
                <div className="section summary-card">
                    <h2 className="section-title">Deal Summary</h2>
                    <p className="section-subtitle">Calculated breakdown</p>

                    {/* Seller Invoice */}
                    <div style={{ marginBottom: '24px', background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#2c3e50', borderBottom: '2px solid #FBBF24', paddingBottom: '8px' }}>Seller Invoice</h3>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Original Tunch to seller</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(originalTunchSeller)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Quantity</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{weightInput || "0"} g</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Fine</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(sellerFineFromTotal)} gm</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Price (without making charges)</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>
                                    {formatINR(sellerSubtotal - ((parseFloat(makingChargesInput) || 0) * (parseFloat(weightInput) || 0)))}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Making Charges</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>
                                    {formatINR((parseFloat(makingChargesInput) || 0) * (parseFloat(weightInput) || 0))}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #FBBF24', marginBottom: '8px' }}>
                            <span style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>Subtotal</span>
                            <span style={{ fontSize: '15px', fontWeight: '600', color: '#FBBF24' }}>{formatINR(sellerSubtotal)}</span>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>GST {gstInput ? `(${gstInput}%)` : ''}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(sellerGstAmount)}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0 0', borderTop: '2px solid #FBBF24' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>Grand Total</span>
                            <span style={{ fontSize: '20px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(sellerGrandTotal)}</span>
                        </div>
                    </div>

                    {/* Buyer Invoice */}
                    <div style={{ marginBottom: '24px', background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#2c3e50', borderBottom: '2px solid #FBBF24', paddingBottom: '8px' }}>Buyer Invoice</h3>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Original Tunch to buyer</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(originalTunchBuyer)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Quantity</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{weightInput || "0"} g</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Fine</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatNumberIN(buyerFineFromTotal)} gm</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Price (without charges)</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(calculatedPriceValueBuyer)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Making Charges</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>
                                    {formatINR((parseFloat(makingChargesInput) || 0) * (parseFloat(weightInput) || 0))}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>Margin Making Charges</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>
                                    {formatINR((parseFloat(wastageMakingChargesInput) || 0) * (parseFloat(weightInput) || 0))}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '2px solid #FBBF24', marginBottom: '8px' }}>
                            <span style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>Subtotal</span>
                            <span style={{ fontSize: '15px', fontWeight: '600', color: '#FBBF24' }}>{formatINR(subtotal)}</span>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ fontSize: '14px', color: '#6c757d' }}>GST {gstInput ? `(${gstInput}%)` : ''}</span>
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(gstAmount)}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0 0', borderTop: '2px solid #FBBF24' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>Grand Total</span>
                            <span style={{ fontSize: '20px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(grandTotal)}</span>
                        </div>
                    </div>

                    {/* Admin Profit - ONLY for Super Admin */}
                    {isSuperAdmin && (
                        <div style={{ marginBottom: '20px', background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#2c3e50', borderBottom: '2px solid #FBBF24', paddingBottom: '8px' }}>Admin Profit</h3>
                            <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Margin Wastage Profit</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(marginWastageProfit)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Margin Making Charges Profit</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>{formatINR(marginMakingChargesProfit)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #dee2e6' }}>
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Total Admin Profit</span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#FBBF24' }}>{formatINR(totalAdminProfit)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Admin Fine</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#FBBF24' }}>
                                        {formatNumberIN(perGramPrice > 0 ? totalAdminProfit / perGramPrice : 0)} gm
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="buttons">
                        <button className="btn btn-generate" onClick={handleGenerateInvoice}>Generate Order</button>
                        <button className="btn btn-whatsapp">Send via WhatsApp</button>
                    </div>
                </div>
            </div>

            {/* Payment Options */}
            <div className="payment-options">
                <div className={`payment-option ${selectedPayment === "COD" ? "active" : ""}`} onClick={() => setSelectedPayment("COD")}>COD</div>
                <div className={`payment-option ${selectedPayment === "UPI" ? "active" : ""}`} onClick={() => setSelectedPayment("UPI")}>UPI</div>
                <div className={`payment-option ${selectedPayment === "BANK TRANSFER" ? "active" : ""}`} onClick={() => setSelectedPayment("BANK TRANSFER")}>BANK TRANSFER</div>
                <div className={`payment-option ${selectedPayment === "METAL PAY" ? "active" : ""}`} onClick={() => setSelectedPayment("METAL PAY")}>METAL PAY</div>
            </div>
        </div>
    );
};

export default AdminDealCalculator;